import React from 'react';
import { useNavigate } from 'react-router-dom';
import ReactDataTable from 'react-data-table-component';
import { TableLoader } from '../../../components/common/Loader';
import {
    statusLabel, statusBadgeClass, priorityBadgeClass, effectivePriority,
    priorityWasReassessed, formatDateTime, dataTableStyles,
} from '../utils';

export function StatusBadge({ ticket }) {
    return (
        <span className={`px-8 py-3 rounded-full text-xs font-medium whitespace-nowrap ${statusBadgeClass(ticket.status)}`}>
            {statusLabel(ticket)}
        </span>
    );
}

// effective_priority always shown; dot marks ones IT re-assessed.
export function PriorityBadge({ ticket }) {
    const priority = effectivePriority(ticket);
    if (!priority) return <span className="text-text-secondary text-sm">-</span>;
    return (
        <span className={`px-8 py-3 rounded-full text-xs font-medium whitespace-nowrap inline-flex items-center gap-4 ${priorityBadgeClass(priority)}`}>
            {priority}
            {priorityWasReassessed(ticket) && <span className="w-[5px] h-[5px] rounded-full bg-current" title="Priority re-assessed by IT" />}
        </span>
    );
}

export default function TicketTable({ tickets, isLoading, subHeaderComponent, emptyMessage, showRequester = false }) {
    const navigate = useNavigate();

    const columns = [
        {
            name: 'Reference',
            selector: row => row.reference_no,
            sortable: true,
            cell: row => (
                <div>
                    <p className="font-medium text-sm">{row.reference_no || '-'}</p>
                    {showRequester && (
                        <p className="text-xs text-text-secondary mt-1">{row.requester_name || ''}</p>
                    )}
                </div>
            ),
            minWidth: '150px',
        },
        {
            name: 'Summary',
            selector: row => row.summary,
            cell: row => (
                <div>
                    <p className="text-sm">{row.summary || '-'}</p>
                    {row.request_type_display && (
                        <p className="text-xs text-text-secondary mt-1">{row.request_type_display}</p>
                    )}
                </div>
            ),
            minWidth: '260px',
        },
        {
            name: 'Priority',
            selector: row => effectivePriority(row) || '',
            sortable: true,
            cell: row => <PriorityBadge ticket={row} />,
            width: '110px',
        },
        {
            name: 'Status',
            selector: row => row.status,
            sortable: true,
            cell: row => <StatusBadge ticket={row} />,
            width: '130px',
        },
        {
            name: 'Assigned To',
            selector: row => row.assignee_name || '',
            cell: row => (
                <span className={`text-sm ${row.assignee_name ? '' : 'text-text-secondary italic'}`}>
                    {row.assignee_name || 'Unassigned'}
                </span>
            ),
            width: '160px',
        },
        {
            name: 'Created',
            selector: row => row.created_at,
            sortable: true,
            cell: row => <span className="text-sm text-text-secondary">{formatDateTime(row.created_at)}</span>,
            width: '170px',
        },
    ];

    if (isLoading) return <TableLoader />;

    return (
        <ReactDataTable
            columns={columns}
            data={tickets}
            pagination
            subHeader={!!subHeaderComponent}
            subHeaderComponent={subHeaderComponent}
            highlightOnHover
            pointerOnHover
            persistTableHead
            onRowClicked={(row) => navigate(`/support/tickets/${row.id}`)}
            noDataComponent={
                <div className="py-40 text-center text-text-secondary text-sm">{emptyMessage}</div>
            }
            customStyles={dataTableStyles}
        />
    );
}
